import { useEffect, useMemo, useState, Fragment } from "react";
import BackofficeLayout, { Card, Btn, KpiCard, fmtEUR } from "./BackofficeLayout";
import { api } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Edit, Receipt, Calendar, TrendingDown, Search, ChevronDown, ChevronRight, Percent, Wallet, Banknote, CalendarClock, Table2, Upload, FileDown, X } from "lucide-react";

const FREQ_LABEL = {
  monthly: "Mensuelle",
  quarterly: "Trimestrielle",
  semi_annual: "Semestrielle",
  annual: "Annuelle",
};
const FREQ_TO_MONTHLY = {
  monthly: 1,
  quarterly: 1 / 3,
  semi_annual: 1 / 6,
  annual: 1 / 12,
};

const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

// ============================================================================
// Helpers de calcul (dérivés depuis les champs existants du prêt)
// ============================================================================

const monthsDiff = (fromISO, toDate) => {
  if (!fromISO) return 0;
  const from = new Date(fromISO);
  if (isNaN(from.getTime())) return 0;
  const y = toDate.getFullYear() - from.getFullYear();
  const m = toDate.getMonth() - from.getMonth();
  return Math.max(0, y * 12 + m);
};

const addMonthsISO = (fromISO, months) => {
  if (!fromISO) return null;
  const d = new Date(fromISO);
  if (isNaN(d.getTime())) return null;
  const day = d.getDate();
  d.setDate(1); // évite le décalage 30/31 quand JS re-map dans un mois plus court
  d.setMonth(d.getMonth() + months);
  // Cap sur le dernier jour du mois si le jour d'origine dépasse
  const lastDayOfTarget = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDayOfTarget));
  return d.toISOString().slice(0, 10);
};

/**
 * Capital restant dû à un instant t (formule d'amortissement standard).
 * CRD(n) = P·(1+i)^n − M·[(1+i)^n − 1]/i
 * Approximation : on calcule au pas mensuel avec mensualité équivalente.
 * NOTE : si le prêt a un tableau d'amortissement importé (loan.crd_from_schedule),
 * cette valeur DOIT être utilisée en priorité (précision au jour près).
 */
const computeCrd = (loan, atDate = new Date()) => {
  if (loan.crd_from_schedule != null && !isNaN(parseFloat(loan.crd_from_schedule))) {
    return parseFloat(loan.crd_from_schedule);
  }
  const principal = parseFloat(loan.principal) || 0;
  const disbursed = parseFloat(loan.disbursed_amount) || 0;
  const rateAnnual = parseFloat(loan.interest_rate) || 0;
  const duration = parseInt(loan.duration_months) || 0;
  const freq = loan.payment_frequency || "monthly";
  const amount = parseFloat(loan.payment_amount || loan.monthly_payment || 0);
  const monthlyEq = amount * (FREQ_TO_MONTHLY[freq] || 1);

  if (!loan.start_date || duration <= 0 || principal <= 0) return disbursed || principal;

  const elapsed = monthsDiff(loan.start_date, atDate);
  if (elapsed <= 0) return disbursed || principal;
  if (elapsed >= duration) return 0;

  const i = rateAnnual / 100 / 12;
  if (i <= 0 || monthlyEq <= 0) {
    // Prêt à taux 0 ou pas d'échéance connue : amortissement linéaire
    return Math.max(0, principal * (1 - elapsed / duration));
  }
  const pow = Math.pow(1 + i, elapsed);
  const crd = principal * pow - monthlyEq * ((pow - 1) / i);
  return Math.max(0, crd);
};

const computeEndDate = (loan) => {
  if (!loan.start_date || !loan.duration_months) return null;
  return addMonthsISO(loan.start_date, parseInt(loan.duration_months));
};

const formatRemaining = (endDateISO, atDate = new Date()) => {
  if (!endDateISO) return "—";
  const end = new Date(endDateISO);
  if (isNaN(end.getTime())) return "—";
  const now = atDate;
  const y = end.getFullYear() - now.getFullYear();
  const m = end.getMonth() - now.getMonth();
  const totalMonths = y * 12 + m;
  if (totalMonths <= 0) return "soldé";
  const yy = Math.floor(totalMonths / 12);
  const mm = totalMonths % 12;
  if (yy === 0) return `${mm} mois`;
  if (mm === 0) return `${yy} an${yy > 1 ? "s" : ""}`;
  return `${yy} an${yy > 1 ? "s" : ""} ${mm} mois`;
};

const monthlyEquivalent = (loan) => {
  const freq = loan.payment_frequency || "monthly";
  const amount = parseFloat(loan.payment_amount || loan.monthly_payment || 0);
  return amount * (FREQ_TO_MONTHLY[freq] || 1);
};

// ============================================================================

const empty = {
  entity_id: "", project_id: "", lender: "",
  principal: 0, interest_rate: 0, duration_months: 0,
  payment_frequency: "monthly", payment_amount: 0,
  payment_day: "", payment_month: "",
  next_payment_date: "",
  start_date: "", disbursed_amount: 0, notes: "",
};

export default function LoansAdmin() {
  const [items, setItems] = useState([]);
  const [entities, setEntities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Filtres & tri
  const [search, setSearch] = useState("");
  const [filterEntity, setFilterEntity] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // "", "active", "closed"
  const [sortBy, setSortBy] = useState("lender");
  const [sortDir, setSortDir] = useState("asc");
  const [groupByEntity, setGroupByEntity] = useState(false);
  const [collapsedEntities, setCollapsedEntities] = useState({});

  // Amortization schedule upload
  const [scheduleLoan, setScheduleLoan] = useState(null); // loan open in modal
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleBusy, setScheduleBusy] = useState(false);

  // Timeline horizon
  const [timelineHorizon, setTimelineHorizon] = useState(12);
  const [selectedTimelineMonth, setSelectedTimelineMonth] = useState(null);

  const load = () =>
    Promise.all([
      api.get("/backoffice/loans").then((r) => r.data),
      api.get("/backoffice/entities").then((r) => r.data),
      api.get("/backoffice/projects").then((r) => r.data),
    ]).then(([l, e, p]) => { setItems(l); setEntities(e); setProjects(p); });
  useEffect(() => { load(); }, []);

  const monthlyEq = (parseFloat(form.payment_amount) || 0) * (FREQ_TO_MONTHLY[form.payment_frequency] || 1);
  const annualEq = monthlyEq * 12;

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      principal: parseFloat(form.principal) || 0,
      interest_rate: parseFloat(form.interest_rate) || 0,
      duration_months: parseInt(form.duration_months) || 0,
      payment_amount: parseFloat(form.payment_amount) || 0,
      payment_day: form.payment_day ? parseInt(form.payment_day) : null,
      payment_month: form.payment_month ? parseInt(form.payment_month) : null,
      next_payment_date: form.next_payment_date || null,
      disbursed_amount: parseFloat(form.disbursed_amount) || 0,
      project_id: form.project_id || null,
      start_date: form.start_date || null,
    };
    try {
      if (editing) {
        await api.put(`/backoffice/loans/${editing}`, payload);
        toast.success("Prêt mis à jour");
      } else {
        await api.post("/backoffice/loans", payload);
        toast.success("Prêt ajouté");
      }
      setShowForm(false); setEditing(null); setForm(empty);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const startEdit = (l) => {
    setEditing(l.id);
    setForm({
      entity_id: l.entity_id, project_id: l.project_id || "",
      lender: l.lender, principal: l.principal,
      interest_rate: l.interest_rate, duration_months: l.duration_months,
      payment_frequency: l.payment_frequency || "monthly",
      payment_amount: l.payment_amount || l.monthly_payment || 0,
      payment_day: l.payment_day || "",
      payment_month: l.payment_month || "",
      next_payment_date: l.next_payment_date || "",
      start_date: l.start_date || "",
      disbursed_amount: l.disbursed_amount, notes: l.notes || "",
    });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce prêt ?")) return;
    await api.delete(`/backoffice/loans/${id}`);
    toast.success("Supprimé");
    load();
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, entity_id: entities[0]?.id || "" });
    setShowForm(true);
  };

  // ==== Schedule (tableau d'amortissement) ====
  const openSchedule = async (loan) => {
    setScheduleLoan(loan);
    setScheduleData(null);
    try {
      const r = await api.get(`/backoffice/loans/${loan.id}/schedule`);
      setScheduleData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const uploadSchedule = async (file) => {
    if (!scheduleLoan || !file) return;
    setScheduleBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/backoffice/loans/${scheduleLoan.id}/schedule/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${r.data.imported} lignes importées${r.data.errors_count ? ` (${r.data.errors_count} erreurs)` : ""}`);
      const detail = await api.get(`/backoffice/loans/${scheduleLoan.id}/schedule`);
      setScheduleData(detail.data);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import échoué");
    } finally {
      setScheduleBusy(false);
    }
  };

  const clearSchedule = async () => {
    if (!scheduleLoan) return;
    if (!window.confirm("Supprimer tout le tableau d'amortissement de ce prêt ?")) return;
    try {
      await api.delete(`/backoffice/loans/${scheduleLoan.id}/schedule`);
      toast.success("Tableau supprimé");
      setScheduleData({ count: 0, past_count: 0, future_count: 0, entries: [] });
      load();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const downloadTemplate = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/backoffice/loans/schedule/template.csv`;
    window.open(url, "_blank");
  };

  const isAnnual = form.payment_frequency === "annual";

  // ==== ENRICHISSEMENT (CRD, date fin, statut, %, etc.) ====
  const enriched = useMemo(() => {
    return items.map((l) => {
      const endDate = computeEndDate(l);
      const crd = computeCrd(l);
      const principal = parseFloat(l.principal) || 0;
      const repaidPct = principal > 0 ? Math.max(0, Math.min(100, ((principal - crd) / principal) * 100)) : 0;
      const mEq = monthlyEquivalent(l);
      const status = crd <= 0.5 ? "closed" : "active";
      return { ...l, _crd: crd, _endDate: endDate, _repaidPct: repaidPct, _monthlyEq: mEq, _annualEq: mEq * 12, _status: status };
    });
  }, [items]);

  // ==== FILTRAGE + TRI ====
  const filtered = useMemo(() => {
    let arr = enriched;
    if (search) {
      const s = search.toLowerCase();
      arr = arr.filter((l) => (l.lender || "").toLowerCase().includes(s) || (l.notes || "").toLowerCase().includes(s));
    }
    if (filterEntity) arr = arr.filter((l) => l.entity_id === filterEntity);
    if (filterStatus) arr = arr.filter((l) => l._status === filterStatus);
    arr = [...arr].sort((a, b) => {
      const sign = sortDir === "asc" ? 1 : -1;
      const av = a[sortBy], bv = b[sortBy];
      if (typeof av === "number" && typeof bv === "number") return sign * (av - bv);
      return sign * String(av || "").localeCompare(String(bv || ""));
    });
    return arr;
  }, [enriched, search, filterEntity, filterStatus, sortBy, sortDir]);

  // ==== KPIs GROUPE (basés sur enriched non filtré = vue globale) ====
  const kpis = useMemo(() => {
    const totalPrincipal = enriched.reduce((s, l) => s + (parseFloat(l.principal) || 0), 0);
    const totalCrd = enriched.reduce((s, l) => s + l._crd, 0);
    const totalMonthly = enriched.reduce((s, l) => s + l._monthlyEq, 0);
    const activeCount = enriched.filter((l) => l._status === "active").length;
    // Taux moyen pondéré par capital restant dû
    const totalCrdW = enriched.reduce((s, l) => s + l._crd, 0) || 1;
    const wRate = enriched.reduce((s, l) => s + (parseFloat(l.interest_rate) || 0) * l._crd, 0) / totalCrdW;
    const repaidPct = totalPrincipal > 0 ? ((totalPrincipal - totalCrd) / totalPrincipal) * 100 : 0;
    return { totalPrincipal, totalCrd, totalMonthly, activeCount, wRate, repaidPct };
  }, [enriched]);

  // ==== GROUPEMENT PAR ENTITÉ ====
  const grouped = useMemo(() => {
    if (!groupByEntity) return null;
    const g = {};
    filtered.forEach((l) => {
      const key = l.entity_id || "none";
      if (!g[key]) g[key] = { entity_id: l.entity_id, entity_name: entities.find((e) => e.id === l.entity_id)?.name || "—", loans: [], crd: 0, monthly: 0 };
      g[key].loans.push(l);
      g[key].crd += l._crd;
      g[key].monthly += l._monthlyEq;
    });
    return Object.values(g);
  }, [filtered, entities, groupByEntity]);

  // ==== TIMELINE cash-flow prêts (agrégée par mois × prêteur) ====
  const timeline = useMemo(() => {
    // Génère les N prochains mois
    const now = new Date();
    const months = [];
    for (let i = 0; i < timelineHorizon; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      months.push({
        ym: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        total: 0,
        events: [],
      });
    }
    // Pour chaque prêt actif : calcule les échéances qui tombent dans le range
    enriched.forEach((l) => {
      if (l._status === "closed") return;
      const amount = parseFloat(l.payment_amount || l.monthly_payment || 0);
      if (amount <= 0) return;
      const freq = l.payment_frequency || "monthly";
      const step = { monthly: 1, quarterly: 3, semi_annual: 6, annual: 12 }[freq] || 1;

      // Trouver le mois d'ancrage : next_payment_date, sinon payment_month, sinon start_date
      let anchor = null;
      if (l.next_payment_date) {
        const d = new Date(l.next_payment_date);
        if (!isNaN(d.getTime())) anchor = { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
      }
      if (!anchor && l.start_date) {
        const d = new Date(l.start_date);
        if (!isNaN(d.getTime())) {
          const day = l.payment_day || d.getDate();
          const month = freq === "annual" && l.payment_month ? l.payment_month : d.getMonth() + 1;
          anchor = { year: d.getFullYear(), month, day };
        }
      }
      if (!anchor) return;

      const anchorIndex = anchor.year * 12 + (anchor.month - 1);
      months.forEach((m) => {
        const idx = m.year * 12 + (m.month - 1);
        const delta = idx - anchorIndex;
        if (delta < 0 || delta % step !== 0) return;
        m.total += amount;
        m.events.push({ lender: l.lender, amount, entity_id: l.entity_id, loan_id: l.id, day: anchor.day });
      });
    });
    return months;
  }, [enriched, timelineHorizon]);

  const timelineMax = useMemo(() => Math.max(1, ...timeline.map((m) => m.total)), [timeline]);
  const timelineTotal = useMemo(() => timeline.reduce((s, m) => s + m.total, 0), [timeline]);
  const timelinePeak = useMemo(() => timeline.reduce((best, m) => (m.total > best.total ? m : best), { total: 0, ym: "-" }), [timeline]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir("asc"); }
  };

  const sortIcon = (key) => sortBy !== key ? "" : sortDir === "asc" ? " ▲" : " ▼";

  return (
    <BackofficeLayout
      title="Prêts bancaires"
      subtitle="Suivi des emprunts par entité / projet. Mensualité, trimestrialité, semestrialité ou annuité — converties automatiquement en équivalent mensuel pour le cash-flow."
      actions={<Btn onClick={openNew} data-testid="bof-loan-new-btn"><Plus className="w-3.5 h-3.5" /> Nouveau prêt</Btn>}
    >
      <Toaster theme="dark" position="top-right" />

      {/* ==== KPI BANNER ==== */}
      {items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="bof-loans-kpis">
          <KpiCard
            icon={<Banknote className="w-4 h-4" />}
            label="Capital emprunté"
            value={fmtEUR(kpis.totalPrincipal)}
            sub={`${enriched.length} prêt${enriched.length > 1 ? "s" : ""} (${kpis.activeCount} actif${kpis.activeCount > 1 ? "s" : ""})`}
            testid="bof-loans-kpi-principal"
          />
          <KpiCard
            icon={<TrendingDown className="w-4 h-4" />}
            label="Capital restant dû"
            value={fmtEUR(kpis.totalCrd)}
            sub={`${kpis.repaidPct.toFixed(1)} % remboursé`}
            tone="amber"
            testid="bof-loans-kpi-crd"
          />
          <KpiCard
            icon={<CalendarClock className="w-4 h-4" />}
            label="Équivalent mensuel"
            value={fmtEUR(kpis.totalMonthly)}
            sub="tous prêts confondus"
            testid="bof-loans-kpi-monthly"
          />
          <KpiCard
            icon={<Percent className="w-4 h-4" />}
            label="Taux moyen pondéré"
            value={`${kpis.wRate.toFixed(2)} %`}
            sub="pondéré par CRD"
            testid="bof-loans-kpi-rate"
          />
          <KpiCard
            icon={<Wallet className="w-4 h-4" />}
            label="Effort annuel"
            value={fmtEUR(kpis.totalMonthly * 12)}
            sub="cash-flow annualisé"
            testid="bof-loans-kpi-annual"
          />
        </div>
      )}

      {/* ==== TIMELINE cash-flow prêts ==== */}
      {enriched.length > 0 && (
        <Card className="mb-6" data-testid="bof-loans-timeline">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                Échéances des prêts (histogramme)
              </div>
              <div className="text-xs text-slate-400 mt-1">
                Décaissement mensuel agrégé — <span className="text-white font-bold">{fmtEUR(timelineTotal)}</span> sur les {timelineHorizon} prochains mois
                {" · "}pic : <span className="text-amber-300 font-bold">{fmtEUR(timelinePeak.total)}</span> en {timelinePeak.ym}
              </div>
            </div>
            <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-700 rounded px-1 py-1">
              {[12, 24, 36].map((h) => (
                <button key={h} onClick={() => setTimelineHorizon(h)}
                        className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded transition ${
                          timelineHorizon === h
                            ? "bg-amber-500/25 text-amber-200"
                            : "text-slate-400 hover:text-white hover:bg-slate-700/60"
                        }`}
                        data-testid={`bof-loans-timeline-h-${h}`}>
                  {h}m
                </button>
              ))}
            </div>
          </div>

          {/* Bars */}
          <div className="overflow-x-auto pb-2">
            <div className="flex items-end gap-[3px] min-w-full" style={{ height: 160 }}>
              {timeline.map((m) => {
                const pct = timelineMax > 0 ? (m.total / timelineMax) * 100 : 0;
                const barHeightPx = m.total > 0 ? Math.max(3, Math.round((pct / 100) * 130)) : 0;
                const label = `${m.month}/${String(m.year).slice(2)}`;
                const isSelected = selectedTimelineMonth?.ym === m.ym;
                const clickable = m.total > 0;
                return (
                  <div key={m.ym}
                       className={`flex-1 min-w-[24px] flex flex-col items-center justify-end gap-1 group relative ${clickable ? "cursor-pointer" : ""}`}
                       style={{ height: 160 }}
                       onClick={() => clickable && setSelectedTimelineMonth(isSelected ? null : m)}
                       data-testid={`bof-timeline-bar-${m.ym}`}>
                    {m.total > 0 && (
                      <div className="absolute -top-1 opacity-0 group-hover:opacity-100 transition text-[9px] text-amber-300 font-bold whitespace-nowrap pointer-events-none z-10 bg-slate-900/90 px-1.5 py-0.5 rounded border border-amber-500/30">
                        {fmtEUR(m.total)}
                      </div>
                    )}
                    <div className={`w-full rounded-t transition-all group-hover:brightness-125 ${isSelected ? "ring-2 ring-white brightness-125" : ""}`}
                         style={{
                           height: `${barHeightPx}px`,
                           background: "linear-gradient(180deg, rgb(251,191,36) 0%, rgb(217,119,6) 100%)",
                         }}
                    />
                    <div className={`text-[8px] font-mono ${isSelected ? "text-white font-bold" : "text-slate-500"}`}>{label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Détail des mois avec échéances */}
          <div className="mt-3 text-[10px] text-slate-500">
            {selectedTimelineMonth ? "Clique à nouveau sur la barre pour masquer" : "Cliquez sur une barre pour voir les prêts qui la composent"}
          </div>

          {/* Panel de détail sur clic */}
          {selectedTimelineMonth && (
            <div className="mt-4 p-4 border border-amber-500/30 bg-amber-500/5 rounded" data-testid="bof-timeline-detail">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
                    Décaissement {selectedTimelineMonth.month}/{selectedTimelineMonth.year}
                  </div>
                  <div className="text-lg font-bold text-white mt-0.5">
                    {fmtEUR(selectedTimelineMonth.total)}
                    <span className="text-xs text-slate-400 ml-2">
                      · {selectedTimelineMonth.events.length} prêt{selectedTimelineMonth.events.length > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
                <button onClick={() => setSelectedTimelineMonth(null)}
                        className="text-slate-500 hover:text-white text-lg leading-none px-2"
                        data-testid="bof-timeline-detail-close">×</button>
              </div>
              <div className="space-y-1.5">
                {[...selectedTimelineMonth.events].sort((a, b) => b.amount - a.amount).map((ev, idx) => {
                  const ent = entities.find((e) => e.id === ev.entity_id);
                  const pct = selectedTimelineMonth.total > 0 ? (ev.amount / selectedTimelineMonth.total) * 100 : 0;
                  return (
                    <div key={`${ev.loan_id}-${idx}`} className="flex items-center gap-3 text-xs" data-testid={`bof-timeline-detail-row-${idx}`}>
                      <div className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-white truncate">{ev.lender}</div>
                        <div className="text-[10px] text-slate-500 truncate">{ent?.name || "—"} · échéance le {ev.day}</div>
                      </div>
                      <div className="w-32 bg-slate-800 rounded-full h-1 overflow-hidden">
                        <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-right font-mono font-bold text-amber-300 w-24">{fmtEUR(ev.amount)}</div>
                      <div className="text-right text-[10px] text-slate-500 w-10">{pct.toFixed(0)}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      )}

      {showForm && (
        <Card className="mb-6" data-testid="bof-loan-form">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Entité">
              <select required value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} className="input" data-testid="bof-loan-entity">
                <option value="">— Choisir —</option>
                {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </F>
            <F label="Projet (optionnel)">
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="input">
                <option value="">— Aucun —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </F>
            <F label="Prêteur">
              <input required value={form.lender} onChange={(e) => setForm({ ...form, lender: e.target.value })} placeholder="Crédit Agricole" className="input" data-testid="bof-loan-lender" />
            </F>

            <F label="Capital emprunté (€)">
              <input type="number" step="0.01" required value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} className="input" data-testid="bof-loan-principal" />
            </F>
            <F label="Taux (%)">
              <input type="number" step="0.01" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} className="input" />
            </F>
            <F label="Durée (mois)">
              <input type="number" value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} className="input" />
            </F>

            <div className="md:col-span-3 mt-2 pt-3 border-t border-slate-800">
              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-3">Échéance de remboursement</div>
            </div>

            <F label="Fréquence">
              <select value={form.payment_frequency} onChange={(e) => setForm({ ...form, payment_frequency: e.target.value })} className="input" data-testid="bof-loan-frequency">
                <option value="monthly">Mensuelle</option>
                <option value="quarterly">Trimestrielle (tous les 3 mois)</option>
                <option value="semi_annual">Semestrielle (tous les 6 mois)</option>
                <option value="annual">Annuelle</option>
              </select>
            </F>
            <F label={`Montant de l'échéance (${FREQ_LABEL[form.payment_frequency]?.toLowerCase() || "mensualité"}) (€)`}>
              <input type="number" step="0.01" value={form.payment_amount}
                     onChange={(e) => setForm({ ...form, payment_amount: e.target.value })}
                     className="input" data-testid="bof-loan-payment-amount" />
            </F>
            <F label="Capital débloqué (€)">
              <input type="number" step="0.01" value={form.disbursed_amount} onChange={(e) => setForm({ ...form, disbursed_amount: e.target.value })} className="input" />
            </F>

            <F label="Jour d'échéance (1-31, optionnel)">
              <input type="number" min="1" max="31" value={form.payment_day}
                     onChange={(e) => setForm({ ...form, payment_day: e.target.value })}
                     placeholder="Ex : 5" className="input" data-testid="bof-loan-payment-day" />
            </F>
            <F label={isAnnual ? "Mois d'échéance (obligatoire pour annuité)" : "Mois d'échéance (optionnel)"}>
              <select value={form.payment_month} onChange={(e) => setForm({ ...form, payment_month: e.target.value })} className="input" data-testid="bof-loan-payment-month">
                <option value="">— Non précisé —</option>
                {MONTHS_FR.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </F>
            <F label="Prochaine échéance (date précise, optionnel)">
              <input type="date" value={form.next_payment_date}
                     onChange={(e) => setForm({ ...form, next_payment_date: e.target.value })}
                     className="input" data-testid="bof-loan-next-date" />
            </F>

            {(parseFloat(form.payment_amount) || 0) > 0 && (
              <div className="md:col-span-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded flex flex-wrap gap-x-6 gap-y-2 text-sm" data-testid="bof-loan-preview">
                <div>
                  <span className="text-slate-400 text-xs uppercase tracking-widest font-semibold">Équivalent mensuel</span>
                  <span className="ml-2 font-bold text-amber-300">{fmtEUR(monthlyEq)}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs uppercase tracking-widest font-semibold">Équivalent annuel</span>
                  <span className="ml-2 font-bold text-amber-300">{fmtEUR(annualEq)}</span>
                </div>
                <div className="text-xs text-slate-500 italic">Utilisé dans les calculs de cash-flow groupe et projet</div>
              </div>
            )}

            <F label="Date de départ du prêt">
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input" />
            </F>
            <F label="Notes" span={2}>
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" />
            </F>

            <div className="md:col-span-3 flex gap-2">
              <Btn type="submit" data-testid="bof-loan-save">{editing ? "Enregistrer" : "Créer"}</Btn>
              <Btn type="button" variant="ghost" onClick={() => { setShowForm(false); setEditing(null); }}>Annuler</Btn>
            </div>
          </form>
        </Card>
      )}

      {/* ==== FILTRES ==== */}
      {items.length > 0 && (
        <Card className="mb-4" data-testid="bof-loans-filters">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Rechercher un prêteur / une note..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-9"
                data-testid="bof-loans-search"
              />
            </div>
            <select value={filterEntity} onChange={(e) => setFilterEntity(e.target.value)} className="input max-w-[220px]" data-testid="bof-loans-filter-entity">
              <option value="">Toutes les entités</option>
              {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="input max-w-[160px]" data-testid="bof-loans-filter-status">
              <option value="">Tous statuts</option>
              <option value="active">Actifs</option>
              <option value="closed">Soldés</option>
            </select>
            <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none">
              <input type="checkbox" checked={groupByEntity} onChange={(e) => setGroupByEntity(e.target.checked)} data-testid="bof-loans-group-toggle" />
              Grouper par entité
            </label>
            <span className="text-[10px] uppercase tracking-widest text-slate-500 ml-auto">
              {filtered.length}/{items.length} affichés
            </span>
          </div>
        </Card>
      )}

      <Card data-testid="bof-loans-list">
        {items.length === 0 ? (
          <div className="text-center text-slate-500 py-8">Aucun prêt enregistré.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <tr className="border-b border-slate-800">
                  <SortableTh label={"Prêteur" + sortIcon("lender")} onClick={() => toggleSort("lender")} />
                  <th className="py-2 pr-4">Entité</th>
                  <SortableTh label={"Capital" + sortIcon("principal")} align="right" onClick={() => toggleSort("principal")} />
                  <SortableTh label={"CRD actuel" + sortIcon("_crd")} align="right" onClick={() => toggleSort("_crd")} />
                  <th className="py-2 pr-4 w-28">Remb.</th>
                  <SortableTh label={"Taux" + sortIcon("interest_rate")} align="right" onClick={() => toggleSort("interest_rate")} />
                  <th className="py-2 pr-4">Fréquence</th>
                  <SortableTh label={"≈/mois" + sortIcon("_monthlyEq")} align="right" onClick={() => toggleSort("_monthlyEq")} />
                  <SortableTh label={"≈/an" + sortIcon("_annualEq")} align="right" onClick={() => toggleSort("_annualEq")} />
                  <th className="py-2 pr-4">Prochaine</th>
                  <th className="py-2 pr-4">Fin</th>
                  <th className="py-2 pr-4">Reste</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {groupByEntity && grouped ? grouped.map((g) => {
                  const collapsed = collapsedEntities[g.entity_id || "none"];
                  return (
                    <Fragment key={"g-" + (g.entity_id || "none")}>
                      <tr className="bg-slate-800/40 border-b border-slate-700">
                        <td colSpan="13" className="py-2 px-2">
                          <button
                            onClick={() => setCollapsedEntities({ ...collapsedEntities, [g.entity_id || "none"]: !collapsed })}
                            className="flex items-center gap-2 w-full text-left"
                            data-testid={`bof-loans-group-header-${g.entity_id || "none"}`}
                          >
                            {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            <span className="font-bold text-white text-xs uppercase tracking-widest">{g.entity_name}</span>
                            <span className="text-[10px] text-slate-400">
                              {g.loans.length} prêt{g.loans.length > 1 ? "s" : ""} · CRD {fmtEUR(g.crd)} · {fmtEUR(g.monthly)}/mois
                            </span>
                          </button>
                        </td>
                      </tr>
                      {!collapsed && g.loans.map((l) => <LoanRow key={l.id} l={l} entities={entities} onEdit={startEdit} onRemove={remove} onSchedule={openSchedule} />)}
                    </Fragment>
                  );
                }) : filtered.map((l) => <LoanRow key={l.id} l={l} entities={entities} onEdit={startEdit} onRemove={remove} onSchedule={openSchedule} />)}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ==== Modal Tableau d'amortissement ==== */}
      {scheduleLoan && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10"
             onClick={(e) => { if (e.target === e.currentTarget) { setScheduleLoan(null); setScheduleData(null); } }}
             data-testid="bof-schedule-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-4xl shadow-2xl">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <div>
                <div className="text-sm font-black uppercase tracking-widest text-white">
                  Tableau d&apos;amortissement — {scheduleLoan.lender}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Capital {fmtEUR(scheduleLoan.principal)} · Départ {scheduleLoan.start_date || "—"} · Fin {scheduleLoan._endDate || "—"}
                </div>
              </div>
              <button onClick={() => { setScheduleLoan(null); setScheduleData(null); }} className="text-slate-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Import zone */}
              <div className="flex items-center gap-3 flex-wrap p-4 border border-dashed border-slate-700 rounded bg-slate-800/30">
                <label className={`inline-flex items-center gap-2 px-3 py-2 rounded border cursor-pointer text-xs uppercase tracking-widest font-bold transition ${scheduleBusy ? "opacity-50" : "border-sky-500/40 text-sky-300 hover:bg-sky-500/10"}`}
                       data-testid="bof-schedule-upload-label">
                  <Upload className="w-3.5 h-3.5" />
                  {scheduleBusy ? "Import en cours…" : "Importer un CSV"}
                  <input type="file" accept=".csv,text/csv" hidden disabled={scheduleBusy}
                         onChange={(e) => uploadSchedule(e.target.files?.[0])}
                         data-testid="bof-schedule-upload-input" />
                </label>
                <button onClick={downloadTemplate}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded border border-slate-700 text-slate-400 hover:text-white text-xs uppercase tracking-widest transition"
                        data-testid="bof-schedule-template-btn">
                  <FileDown className="w-3.5 h-3.5" /> Télécharger modèle
                </button>
                {scheduleData?.count > 0 && (
                  <button onClick={clearSchedule}
                          className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs uppercase tracking-widest transition"
                          data-testid="bof-schedule-clear-btn">
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </button>
                )}
                <div className="w-full text-[10px] text-slate-500 leading-relaxed">
                  Colonnes attendues : <code>date, capital_repaid, interest_paid, insurance_paid, crd_after</code>.
                  Séparateur virgule ou point-virgule, décimales avec virgule ou point.
                  L&apos;import <b>remplace</b> tout tableau existant.
                </div>
              </div>

              {/* KPIs si schedule chargé */}
              {scheduleData?.count > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 rounded bg-slate-800/50 border border-slate-700">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Lignes</div>
                    <div className="text-lg font-bold text-white">{scheduleData.count}</div>
                    <div className="text-[10px] text-slate-500">{scheduleData.past_count} passées · {scheduleData.future_count} à venir</div>
                  </div>
                  <div className="p-3 rounded bg-amber-500/10 border border-amber-500/30">
                    <div className="text-[10px] uppercase tracking-widest text-amber-400">CRD actuel</div>
                    <div className="text-lg font-bold text-amber-300">{scheduleData.crd_current != null ? fmtEUR(scheduleData.crd_current) : "—"}</div>
                    <div className="text-[10px] text-slate-500">au jour près</div>
                  </div>
                  <div className="p-3 rounded bg-slate-800/50 border border-slate-700">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Capital remboursé (cumul)</div>
                    <div className="text-lg font-bold text-emerald-300">{fmtEUR(scheduleData.capital_paid_total)}</div>
                  </div>
                  <div className="p-3 rounded bg-slate-800/50 border border-slate-700">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Intérêts payés (cumul)</div>
                    <div className="text-lg font-bold text-red-300">{fmtEUR(scheduleData.interest_paid_total)}</div>
                  </div>
                </div>
              )}

              {/* Table entries */}
              {scheduleData?.entries?.length > 0 ? (
                <div className="max-h-96 overflow-y-auto border border-slate-800 rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-800/60 sticky top-0 text-[10px] uppercase tracking-widest text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-right">Capital</th>
                        <th className="px-3 py-2 text-right">Intérêts</th>
                        <th className="px-3 py-2 text-right">Assurance</th>
                        <th className="px-3 py-2 text-right">CRD après</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scheduleData.entries.map((e) => {
                        const isPast = e.date <= new Date().toISOString().slice(0, 10);
                        return (
                          <tr key={e.id} className={`border-t border-slate-800/50 ${isPast ? "text-slate-500" : "text-white"}`}>
                            <td className="px-3 py-1.5 font-mono">{e.date}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{fmtEUR(e.capital_repaid)}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-red-300/70">{fmtEUR(e.interest_paid)}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-slate-400">{e.insurance_paid ? fmtEUR(e.insurance_paid) : "—"}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-amber-300">{e.crd_after != null ? fmtEUR(e.crd_after) : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : scheduleData && (
                <div className="text-center py-8 text-sm text-slate-500 italic">
                  Aucun tableau importé pour ce prêt. Utilise le bouton ci-dessus pour uploader ton CSV banque.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input { background: rgba(15, 23, 42, 0.6); border: 1px solid rgb(51, 65, 85); color: white;
                 padding: 0.5rem 0.75rem; border-radius: 0.375rem; width: 100%; font-size: 0.875rem; }
        .input:focus { outline: 2px solid rgb(251, 191, 36); outline-offset: -1px; }
      `}</style>
    </BackofficeLayout>
  );
}

const SortableTh = ({ label, align = "left", onClick }) => (
  <th className={`py-2 pr-4 cursor-pointer select-none hover:text-amber-300 transition ${align === "right" ? "text-right" : ""}`} onClick={onClick}>
    {label}
  </th>
);

const LoanRow = ({ l, entities, onEdit, onRemove, onSchedule }) => {
  const ent = entities.find((e) => e.id === l.entity_id);
  const freq = l.payment_frequency || "monthly";
  const amount = l.payment_amount || l.monthly_payment || 0;
  const monthlyEq = l._monthlyEq;
  const nextInfo = l.next_payment_date
    ? l.next_payment_date
    : (l.payment_day && l.payment_month
      ? `${l.payment_day}/${l.payment_month.toString().padStart(2, "0")}`
      : (l.payment_day ? `Le ${l.payment_day}` : "—"));
  const closed = l._status === "closed";
  return (
    <tr className="border-b border-slate-800/50" data-testid={`bof-loan-${l.id}`}>
      <td className="py-2 pr-4 font-bold text-white">
        <div className="flex items-center gap-2 flex-wrap">
          <Receipt className="w-3.5 h-3.5 text-amber-400" /> {l.lender}
          {closed && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-300">Soldé</span>}
          {l.has_schedule && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-sky-500/15 border border-sky-500/30 text-sky-300" title={`${l.schedule_count} lignes importées`}>Tableau</span>}
        </div>
      </td>
      <td className="py-2 pr-4 text-xs text-slate-400">{ent?.name || "—"}</td>
      <td className="py-2 pr-4 text-right font-mono">{fmtEUR(l.principal)}</td>
      <td className="py-2 pr-4 text-right font-mono text-amber-300 font-bold">{fmtEUR(l._crd)}</td>
      <td className="py-2 pr-4" data-testid={`bof-loan-progress-${l.id}`}>
        <div className="w-24 bg-slate-800 rounded-full h-1.5 overflow-hidden">
          <div className="bg-emerald-500 h-full rounded-full transition-all" style={{ width: `${l._repaidPct}%` }} />
        </div>
        <div className="text-[9px] text-slate-500 mt-0.5">{l._repaidPct.toFixed(1)}%</div>
      </td>
      <td className="py-2 pr-4 text-right font-mono">{l.interest_rate}%</td>
      <td className="py-2 pr-4 text-xs">
        <span className="px-2 py-0.5 rounded bg-slate-800 border border-slate-700">
          {FREQ_LABEL[freq]}
        </span>
      </td>
      <td className="py-2 pr-4 text-right font-mono text-amber-300 font-bold" data-testid={`bof-loan-monthly-${l.id}`}>{fmtEUR(monthlyEq)}</td>
      <td className="py-2 pr-4 text-right font-mono text-amber-200 font-bold" data-testid={`bof-loan-annual-${l.id}`}>{fmtEUR(monthlyEq * 12)}</td>
      <td className="py-2 pr-4 text-xs text-slate-400">
        <div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {nextInfo}</div>
      </td>
      <td className="py-2 pr-4 text-xs text-slate-400" data-testid={`bof-loan-end-${l.id}`}>{l._endDate || "—"}</td>
      <td className="py-2 pr-4 text-xs text-slate-400" data-testid={`bof-loan-remaining-${l.id}`}>{formatRemaining(l._endDate)}</td>
      <td className="py-2 pr-2 whitespace-nowrap">
        <button onClick={() => onSchedule(l)}
                className={`mr-2 transition ${l.has_schedule ? "text-emerald-300 hover:text-emerald-100" : "text-slate-500 hover:text-white"}`}
                title={l.has_schedule ? `Tableau chargé (${l.schedule_count} lignes)` : "Charger un tableau d'amortissement"}
                data-testid={`bof-loan-schedule-${l.id}`}>
          <Table2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onEdit(l)} className="text-slate-400 hover:text-white mr-2" data-testid={`bof-loan-edit-${l.id}`}>
          <Edit className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onRemove(l.id)} className="text-red-400 hover:text-red-300" data-testid={`bof-loan-delete-${l.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
};

const F = ({ label, children, span = 1 }) => (
  <label className={`block ${span === 2 ? "md:col-span-2" : ""} ${span === 3 ? "md:col-span-3" : ""}`}>
    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">{label}</span>
    {children}
  </label>
);
